(function () {
  const PLACEHOLDER_IMG =
    "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

  class HeadboardSwatchPicker extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === "true") return;
      this.dataset.initialized = "true";

      this.colourInputs = Array.from(this.querySelectorAll("[data-hsp-colour-input]"));
      this.liveRegion = this.querySelector("[data-hsp-live-region]");
      this.previewImage = this.querySelector("[data-hsp-preview-image]");
      this.previewFallback = this.querySelector("[data-hsp-preview-fallback]");
      this.selectedCountEl = this.querySelector("[data-hsp-selected-count]");
      this.colourNamesEl = this.querySelector("[data-hsp-colour-names]");
      this.statusEl = this.querySelector("[data-hsp-status]");
      this.resetBtn = this.querySelector("[data-hsp-reset]");
      this.addBtn = this.querySelector("[data-hsp-add]");

      this.variants = this.readVariants();
      this.colorIndex = Number(this.dataset.colorIndex || 0);
      this.sizeIndex = Number(this.dataset.sizeIndex || 1);
      this.maxSwatches = Math.min(5, Math.max(1, Number(this.dataset.maxSwatches || 5)));
      this.fixedSize = typeof this.dataset.fixedSize === "string" ? this.dataset.fixedSize.trim() : "";
      this.sizeOptionsCount = Number(this.dataset.sizeOptionsCount || 0);
      this.colorOnly = this.detectColourOnlyCatalog();
      this.emptyPreviewHex = this.dataset.emptyPreviewHex || "#e8e8e8";

      if (this.addBtn) this.addLabelDefault = this.addBtn.textContent.trim();

      this.bindEvents();
      this.applyConfigGuard();
      this.updateUnavailableState();
      this.refreshCapState();
      this.refreshLabel();
      this.resetPreviewVisual();
    }

    readVariants() {
      const script = this.querySelector("[data-hsp-variants]");
      if (!script) return [];
      try {
        const parsed = JSON.parse(script.textContent || "[]");
        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        console.error("Headboard swatch picker: unable to parse variant data", error);
        return [];
      }
    }

    detectColourOnlyCatalog() {
      if (this.dataset.colorOnly === "true") return true;
      const first = this.variants[0];
      const opts = first && Array.isArray(first.options) ? first.options : [];
      /** Colour-only SKU shape: one option per variant (e.g. Color only — see headboard-fabric-swatch). */
      if (opts.length === 1) return true;
      return false;
    }

    get requiresConfiguredSize() {
      return !this.colorOnly && this.sizeOptionsCount > 1 && !this.fixedSize;
    }

    get addingLabelText() {
      const fromAttr = typeof this.dataset.labelAdding === "string" ? this.dataset.labelAdding.trim() : "";
      return fromAttr || "Adding…";
    }

    applyConfigGuard() {
      if (this.requiresConfiguredSize) {
        this.dataset.configErrorShown = "true";
        this.showPersistentConfigError(
          "Set Default size in the theme editor — this product has multiple sizes.",
        );
        if (this.addBtn) this.addBtn.disabled = true;
        this.colourInputs.forEach((input) => {
          input.disabled = true;
        });
      }
    }

    variantAvailableForColour(colourValue) {
      return this.variants.some((variant) => {
        const options = variant.options || [];
        if (options[this.colorIndex] !== colourValue) return false;
        if (this.colorOnly) return variant.available;
        if (!this.fixedSize) return false;
        return options[this.sizeIndex] === this.fixedSize && variant.available;
      });
    }

    updateUnavailableState() {
      if (this.requiresConfiguredSize) return;

      this.colourInputs.forEach((input) => {
        if (!this.variantAvailableForColour(input.value)) {
          input.disabled = true;
          if (input.checked) input.checked = false;
        }
      });

      this.refreshLabel();
      this.refreshCapState();
      const selected = this.getSelectedInputs();
      if (!selected.length) this.resetPreviewVisual();
      else this.paintPreview(selected[selected.length - 1]);
    }

    bindEvents() {
      this.colourInputs.forEach((input) => {
        input.addEventListener("change", (event) => {
          this.handleColourChange(event);
        });
      });

      if (this.resetBtn) {
        this.resetBtn.addEventListener("click", () => {
          this.resetSelection();
        });
      }

      if (this.addBtn) {
        this.addBtn.addEventListener("click", () => {
          void this.addSelectedToCart();
        });
      }
    }

    handleColourChange(event) {
      if (this.requiresConfiguredSize) return;

      const input = /** @type {HTMLInputElement} */ (event.target);
      const label = input.closest(".hsp__swatch-item");

      if (input.checked) {
        const count = this.selectedCount();
        if (count > this.maxSwatches) {
          input.checked = false;
          if (label) {
            label.classList.add("is-at-cap-attempt");
            window.setTimeout(() => label.classList.remove("is-at-cap-attempt"), 400);
          }
          this.announceLive(`You can select up to ${this.maxSwatches}.`);
          return;
        }
      }

      const selected = this.getSelectedInputs();
      if (input.checked) {
        this.paintPreview(input);
      } else if (selected.length) {
        this.paintPreview(selected[selected.length - 1]);
      } else {
        this.resetPreviewVisual();
      }

      this.refreshCapState();
      this.refreshLabel();
      this.clearTransientStatus();
    }

    selectedCount() {
      return this.colourInputs.filter((i) => i.checked).length;
    }

    getSelectedInputs() {
      return this.colourInputs.filter((i) => i.checked);
    }

    refreshCapState() {
      if (this.requiresConfiguredSize) return;

      const atCap = this.selectedCount() >= this.maxSwatches;

      this.colourInputs.forEach((input) => {
        const noVariant = !this.variantAvailableForColour(input.value);
        if (noVariant) {
          input.disabled = true;
          return;
        }
        input.disabled = !input.checked && atCap;
      });
    }

    announceLive(message) {
      if (!this.liveRegion) return;
      this.liveRegion.textContent = message;
      window.setTimeout(() => {
        if (this.liveRegion) this.liveRegion.textContent = "";
      }, 4500);
    }

    refreshLabel() {
      const selected = this.getSelectedInputs().map((i) => i.value);
      const n = selected.length;
      if (this.selectedCountEl) this.selectedCountEl.textContent = String(n);
      if (this.colourNamesEl) {
        this.colourNamesEl.textContent = n ? ` — ${selected.join(", ")}` : "";
      }
    }

    paintPreview(input) {
      if (!(input instanceof HTMLInputElement) || !input.checked) return;

      const imageUrl = input.dataset.hspSwatchImage || "";
      const hex = input.dataset.hspHex || "#D9D9D9";
      const label = input.value;

      if (this.previewImage) {
        if (imageUrl) {
          this.previewImage.src = imageUrl;
          this.previewImage.alt = label;
          this.previewImage.classList.remove("is-hidden");
        } else {
          this.previewImage.src = PLACEHOLDER_IMG;
          this.previewImage.alt = "";
          this.previewImage.classList.add("is-hidden");
        }
      }

      if (this.previewFallback) {
        this.previewFallback.style.setProperty("--hsp-swatch-color", hex);
        if (imageUrl) {
          this.previewFallback.classList.add("is-hidden");
        } else {
          this.previewFallback.classList.remove("is-hidden");
        }
      }
    }

    resetPreviewVisual() {
      if (this.previewImage) {
        this.previewImage.src = PLACEHOLDER_IMG;
        this.previewImage.alt = "";
        this.previewImage.classList.add("is-hidden");
      }
      if (this.previewFallback) {
        this.previewFallback.style.setProperty("--hsp-swatch-color", this.emptyPreviewHex);
        this.previewFallback.classList.remove("is-hidden");
      }
    }

    resetSelection() {
      if (this.requiresConfiguredSize) return;

      this.colourInputs.forEach((input) => {
        if (!this.variantAvailableForColour(input.value)) return;
        input.checked = false;
      });

      this.refreshCapState();
      this.refreshLabel();
      this.resetPreviewVisual();
      this.clearTransientStatus();
      this.announceLive("Selection cleared.");
    }

    showPersistentConfigError(message) {
      if (!this.statusEl) return;
      this.statusEl.hidden = false;
      this.statusEl.removeAttribute("data-transient");
      this.statusEl.dataset.hspStatusType = "error";
      this.statusEl.textContent = message;
    }

    clearTransientStatus() {
      if (!this.statusEl || this.dataset.configErrorShown === "true") return;
      if (this.statusEl.getAttribute("data-transient") !== "true") return;
      this.statusEl.hidden = true;
      this.statusEl.textContent = "";
      this.statusEl.removeAttribute("data-transient");
    }

    showTransient(message, type) {
      if (!this.statusEl || this.dataset.configErrorShown === "true") return;
      this.statusEl.hidden = false;
      this.statusEl.setAttribute("data-transient", "true");
      this.statusEl.dataset.hspStatusType = type === "success" ? "success" : "error";
      this.statusEl.textContent = message;
    }

    resolveVariantRecord(colourValue) {
      const matchAvailable = () =>
        this.variants.find((variant) => {
          const options = variant.options || [];
          if (options[this.colorIndex] !== colourValue) return false;
          if (this.colorOnly) return variant.available;
          if (!this.fixedSize) return false;
          return options[this.sizeIndex] === this.fixedSize && variant.available;
        });

      const matchAny = () =>
        this.variants.find((variant) => {
          const options = variant.options || [];
          if (options[this.colorIndex] !== colourValue) return false;
          if (this.colorOnly) return true;
          if (!this.fixedSize) return false;
          return options[this.sizeIndex] === this.fixedSize;
        });

      return matchAvailable() || matchAny() || null;
    }

    async addSelectedToCart() {
      if (this.requiresConfiguredSize || !this.addBtn) return;

      const selectedInputs = this.getSelectedInputs();

      if (selectedInputs.length === 0) {
        this.showTransient("Select at least one fabric.", "error");
        return;
      }

      const items = [];
      for (const input of selectedInputs) {
        const variant = this.resolveVariantRecord(input.value);
        if (!variant || !variant.available) continue;
        items.push({ id: String(variant.id), quantity: 1 });
      }

      if (!items.length) {
        this.showTransient("Nothing available to add for the current selections.", "error");
        return;
      }

      const addLabelNormal = this.addLabelDefault || "Add selected to cart";
      const loadingLabel = this.addingLabelText;

      this.addBtn.disabled = true;
      this.addBtn.textContent = loadingLabel;

      try {
        const response = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ items }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.description || "Failed to add items to cart");
        }

        this.showTransient(`${items.length} item(s) added to cart.`, "success");

        document.dispatchEvent(new CustomEvent("cart:updated"));

        if (typeof window.theme !== "undefined" && window.theme.cart) {
          if (typeof window.theme.cart.updateCartDrawer === "function") {
            window.theme.cart.updateCartDrawer();
          }
        }
      } catch (error) {
        console.error("Headboard swatch picker cart error", error);
        this.showTransient(error.message || "Could not add to cart.", "error");
      } finally {
        this.addBtn.disabled = false;
        this.addBtn.textContent = addLabelNormal;
      }
    }
  }

  if (!customElements.get("headboard-swatch-picker")) {
    customElements.define("headboard-swatch-picker", HeadboardSwatchPicker);
  }
})();
